import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Language = "nl" | "en" | "fr" | "de" | "es";

interface TranslationContextType {
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
  translate: (text: string) => Promise<string>;
  t: (text: string) => string;
  isTranslating: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// In-memory cache voor vertalingen
const translationCache = new Map<string, Map<Language, string>>();

export function TranslationProvider({ children }: { children: React.ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("app-language");
    return (saved as Language) || "nl";
  });
  const [isTranslating, setIsTranslating] = useState(false);
  const [translations, setTranslations] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    localStorage.setItem("app-language", currentLanguage);
  }, [currentLanguage]);

  const translate = useCallback(async (text: string): Promise<string> => {
    if (!text || currentLanguage === "nl") return text;

    // Check cache first
    const languageCache = translationCache.get(text);
    if (languageCache?.has(currentLanguage)) {
      return languageCache.get(currentLanguage)!;
    }

    try {
      setIsTranslating(true);
      
      const { data, error } = await supabase.functions.invoke("translate", {
        body: {
          text,
          targetLanguage: currentLanguage,
          sourceLanguage: "nl"
        }
      });

      if (error) throw error;
      
      const translatedText = data?.translatedText || text;
      
      // Update cache
      if (!translationCache.has(text)) {
        translationCache.set(text, new Map());
      }
      translationCache.get(text)!.set(currentLanguage, translatedText);
      
      return translatedText;
    } catch (error) {
      console.error("Translation error:", error);
      toast.error("Vertaling mislukt");
      return text;
    } finally {
      setIsTranslating(false);
    }
  }, [currentLanguage]);

  // Synchrone functie die de cache gebruikt
  const t = useCallback((text: string): string => {
    if (!text || currentLanguage === "nl") return text;
    
    const cached = translationCache.get(text)?.get(currentLanguage);
    if (cached) return cached;
    
    // Als niet in cache, vertaal asynchroon en retourneer origineel
    translate(text).then(translated => {
      setTranslations(prev => new Map(prev).set(text, translated));
    });
    
    return translations.get(text) || text;
  }, [currentLanguage, translate, translations]);

  const setLanguage = useCallback((lang: Language) => {
    setCurrentLanguage(lang);
    toast.success(`Taal gewijzigd naar ${getLanguageName(lang)}`);
  }, []);

  return (
    <TranslationContext.Provider value={{ currentLanguage, setLanguage, translate, t, isTranslating }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within TranslationProvider");
  }
  return context;
}

function getLanguageName(lang: Language): string {
  const names: Record<Language, string> = {
    nl: "Nederlands",
    en: "English",
    fr: "Français",
    de: "Deutsch",
    es: "Español"
  };
  return names[lang];
}
